import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { platform, isPluginAvailable } from './platform';

export type NowPlayingPlaybackState = 'playing' | 'paused' | 'stopped';

export interface NowPlayingTrack {
    title: string;
    artist: string;
    album?: string;
    packageName: string;
    appLabel: string;
    state: NowPlayingPlaybackState;
    positionMs?: number;
    durationMs?: number;
}

export interface NowPlayingState {
    supported: boolean;
    enabled: boolean;
    current: NowPlayingTrack | null;
}

interface MusicListenerPlugin {
    isEnabled(): Promise<{ enabled: boolean }>;
    openSettings(): Promise<void>;
    getCurrent(): Promise<unknown>;
    addListener(
        eventName: 'nowPlayingChanged',
        listenerFunc: (state: unknown) => void,
    ): Promise<PluginListenerHandle>;
}

const MusicListener = registerPlugin<MusicListenerPlugin>('MusicListener');

const DISABLED: NowPlayingState = {
    supported: false,
    enabled: false,
    current: null,
};

export function normalizeNowPlayingState(value: unknown, supported = true): NowPlayingState {
    if (!isRecord(value)) return { supported, enabled: false, current: null };
    const enabled = value.enabled === true;
    if (!enabled) return { supported, enabled: false, current: null };

    const current = normalizeTrack(value.current);
    return { supported, enabled: true, current };
}

export async function getNowPlayingState(): Promise<NowPlayingState> {
    if (!canUseMusicListener()) return DISABLED;
    try {
        return normalizeNowPlayingState(await MusicListener.getCurrent(), true);
    } catch {
        return { supported: true, enabled: false, current: null };
    }
}

export async function openNowPlayingSettings(): Promise<void> {
    if (!canUseMusicListener()) return;
    await MusicListener.openSettings();
}

export async function addNowPlayingListener(listener: (state: NowPlayingState) => void): Promise<() => void> {
    if (!canUseMusicListener()) return () => { /* noop */ };
    const handle = await MusicListener.addListener('nowPlayingChanged', (state) => {
        listener(normalizeNowPlayingState(state, true));
    });
    return () => { void handle.remove(); };
}

export function canUseMusicListener(): boolean {
    return platform() === 'android'
        && Capacitor.isNativePlatform()
        && isPluginAvailable('MusicListener');
}

function normalizeTrack(value: unknown): NowPlayingTrack | null {
    if (!isRecord(value)) return null;

    const title = cappedString(value.title);
    const artist = cappedString(value.artist);
    const packageName = cappedString(value.packageName);
    const appLabel = cappedString(value.appLabel);
    const state = normalizePlaybackState(value.state);

    if (!packageName || !appLabel) return null;
    if (!title && !artist) return null;

    const track: NowPlayingTrack = {
        title,
        artist,
        packageName,
        appLabel,
        state,
    };

    const album = cappedString(value.album);
    if (album) track.album = album;

    const positionMs = nonNegativeNumber(value.positionMs);
    if (positionMs !== null) track.positionMs = positionMs;

    const durationMs = nonNegativeNumber(value.durationMs);
    if (durationMs !== null) track.durationMs = durationMs;

    return track;
}

function normalizePlaybackState(value: unknown): NowPlayingPlaybackState {
    return value === 'playing' || value === 'paused' || value === 'stopped'
        ? value
        : 'stopped';
}

function cappedString(value: unknown): string {
    if (typeof value !== 'string') return '';
    const trimmed = value.trim();
    return trimmed.length <= 200 ? trimmed : trimmed.slice(0, 200);
}

function nonNegativeNumber(value: unknown): number | null {
    if (typeof value !== 'number') return null;
    if (!Number.isFinite(value) || value < 0) return null;
    return value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}
